import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import toast from 'react-hot-toast'
import { apiClient } from '../lib/api'
import { X } from 'lucide-react'

export default function LoginPage() {
  const navigate = useNavigate()
  const setAuth = useAuthStore(state => state.setAuth)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [bgLoaded, setBgLoaded] = useState(false)

  useEffect(() => {
    const img = new Image()
    img.src = '/auth-bg.png'
    img.onload = () => setBgLoaded(true)
    img.onerror = () => setBgLoaded(true) // Still show page even if image fails
  }, [])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const response = await apiClient.post('/api/auth/login', {
        email,
        password,
      })

      const { user, token } = response.data

      setAuth(user, token)
      toast.success('Login successful!')
      navigate('/app/pos', { replace: true })
    } catch (error: any) {
      // Fortune 500 Standard: Detailed error handling with user-friendly messages
      let errorMessage = 'Login failed';
      
      if (!error.response) {
        // Network error - no response from server
        errorMessage = 'Cannot connect to server. Please check your internet connection.';
        console.error('Network Error:', {
          message: error.message,
          code: error.code,
          config: error.config
        });
      } else if (error.response.status === 401) {
        // Invalid credentials
        errorMessage = error.response.data?.error || 'Invalid email or password';
      } else if (error.response.status === 500) {
        // Server error
        errorMessage = 'Server error. Please try again later.';
        console.error('Server Error:', error.response.data);
      } else {
        // Other errors
        errorMessage = error.response.data?.error || `Error: ${error.response.status}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false)
    }
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat bg-fixed relative overflow-hidden"
      style={bgLoaded ? { backgroundImage: 'url(/auth-bg.png)', backgroundSize: 'cover' } : { background: '#1a1a1a' }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900/70 via-gray-900/60 to-gray-900/70" />

      {/* Close Button */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-6 right-6 z-20 p-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full hover:bg-white/20 transition-all duration-200 group"
        aria-label="Back to home"
      >
        <X className="w-6 h-6 text-white group-hover:rotate-90 transition-transform duration-300" />
      </button>

      {/* Glass morphism card */}
      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="bg-white/2 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-white/5 to-white/10 px-8 py-6 border-b border-white/10">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-white drop-shadow-2xl tracking-tight">
                Elysium POS
              </h1>
              <p className="text-white/80 mt-2 font-light text-sm tracking-wide">
                Professional Point of Sale System
              </p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin} className="p-8 space-y-6">
            <div className="space-y-2">
              <label className="block text-white/90 font-medium text-sm tracking-wide">
                Email Address
              </label>
              <input 
                type="email" 
                className="w-full px-4 py-3 bg-white/15 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-transparent transition-all duration-200" 
                placeholder="admin@elysium.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required 
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <label className="block text-white/90 font-medium text-sm tracking-wide">
                Password
              </label>
              <input 
                type="password" 
                className="w-full px-4 py-3 bg-white/15 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-transparent transition-all duration-200" 
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required 
              />
            </div>

            <button 
              type="submit" 
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-3.5 rounded-xl font-semibold tracking-wide transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98]"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="px-8 py-4 bg-white/5 border-t border-white/10">
            <p className="text-center text-white/60 text-xs">
              © 2024 Elysium POS. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}